import React, { useState, useEffect, useCallback } from 'react';
import { eventsApi } from '../../services/api';
import { usePermissions } from '../../hooks/usePermissions';
import { isForbidden } from '../../utils/accessDenied';
import toast from 'react-hot-toast';
import { HiOutlinePlus, HiOutlineCalendar, HiOutlineAcademicCap, HiPencil, HiTrash, HiEye, HiX } from 'react-icons/hi';
import EventForm from '../../components/Forms/EventForm';
import TrainingProgramForm from '../../components/Forms/TrainingProgramForm';
import { formatCalendarDateDMY } from '../../utils/timezoneDisplay';

const Events = () => {
  const { canRead } = usePermissions();
  const [events, setEvents] = useState([]);
  const [trainings, setTrainings] = useState([]);
  const [activeTab, setActiveTab] = useState('events');
  const [loading, setLoading] = useState(true);
  const [showEventForm, setShowEventForm] = useState(false);
  const [showTrainingForm, setShowTrainingForm] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedTraining, setSelectedTraining] = useState(null);
  const [showEventDetail, setShowEventDetail] = useState(false);
  const [showTrainingDetail, setShowTrainingDetail] = useState(false);
  const [detailEvent, setDetailEvent] = useState(null);
  const [detailTraining, setDetailTraining] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteType, setDeleteType] = useState(null);

  const fetchData = useCallback(async () => {
    if (!canRead('events')) {
      setEvents([]);
      setTrainings([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [eventsRes, trainingsRes] = await Promise.all([
        eventsApi.getAll(),
        eventsApi.getTrainingPrograms()
      ]);
      setEvents(eventsRes.data.data || []);
      setTrainings(trainingsRes.data.data || []);
    } catch (error) {
      if (!isForbidden(error)) {
        toast.error('Failed to fetch events');
      }
      setEvents([]);
      setTrainings([]);
    } finally {
      setLoading(false);
    }
  }, [canRead]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateEvent = () => {
    setSelectedEvent(null);
    setShowEventForm(true);
  };

  const handleCreateTraining = () => {
    setSelectedTraining(null);
    setShowTrainingForm(true);
  };

  const handleEditEvent = async (event) => {
    try {
      const response = await eventsApi.getById(event.id);
      setSelectedEvent(response.data.data);
      setShowEventForm(true);
    } catch (error) {
      toast.error('Failed to load event details');
    }
  };

  const handleEditTraining = async (training) => {
    try {
      const response = await eventsApi.getTrainingProgramById(training.id);
      setSelectedTraining(response.data.data);
      setShowTrainingForm(true);
    } catch (error) {
      toast.error('Failed to load training program details');
    }
  };

  const handleViewEvent = async (event) => {
    try {
      const response = await eventsApi.getById(event.id);
      setDetailEvent(response.data.data);
      setShowEventDetail(true);
    } catch (error) {
      toast.error('Failed to load event details');
    }
  };

  const handleViewTraining = (training) => {
    setDetailTraining(training);
    setShowTrainingDetail(true);
  };

  const handleDelete = (item, type) => {
    setItemToDelete(item);
    setDeleteType(type);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete || !deleteType) return;

    try {
      if (deleteType === 'event') {
        await eventsApi.delete(itemToDelete.id);
        toast.success('Event deleted successfully');
      } else {
        await eventsApi.deleteTrainingProgram(itemToDelete.id);
        toast.success('Training program deleted successfully');
      }
      setShowDeleteConfirm(false);
      setItemToDelete(null);
      setDeleteType(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete');
    }
  };

  const handleFormSuccess = () => {
    fetchData();
  };

  if (!loading && !canRead('events')) {
    return (
      <div className="card p-8 text-center text-gray-600 space-y-2 max-w-lg mx-auto">
        <p className="font-semibold text-gray-900">No access to events</p>
        <p className="text-sm">
          Your role does not include <strong>events</strong> read permission. Ask an administrator if you need
          access to workshops and training programs.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-gray-900">Events & Training</h1>
          <p className="text-gray-500 mt-1">Workshops, seminars, and training programs</p>
        </div>
        <button 
          onClick={activeTab === 'events' ? handleCreateEvent : handleCreateTraining}
          className="btn btn-primary"
        >
          <HiOutlinePlus className="w-5 h-5 mr-2" />
          {activeTab === 'events' ? 'New Event' : 'New Training Program'}
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 bg-gray-100 p-1 rounded-xl w-fit">
        <button
          onClick={() => setActiveTab('events')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'events'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <HiOutlineCalendar className="w-4 h-4" />
          Events
        </button>
        <button
          onClick={() => setActiveTab('training')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'training'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <HiOutlineAcademicCap className="w-4 h-4" />
          Training Programs
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          <div className="col-span-full flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent"></div>
          </div>
        ) : activeTab === 'events' ? (
          events.length === 0 ? (
            <div className="col-span-full text-center py-12 text-gray-500">
              <HiOutlineCalendar className="w-12 h-12 mx-auto text-gray-300 mb-2" />
              <p>No events found</p>
            </div>
          ) : (
            events.map(event => (
              <div key={event.id} className="card relative">
                <div className="flex justify-between items-start mb-3">
                  <span className={`badge ${
                    event.status === 'upcoming' ? 'bg-blue-100 text-blue-700' :
                    event.status === 'completed' ? 'bg-green-100 text-green-700' :
                    event.status === 'ongoing' ? 'bg-amber-100 text-amber-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {event.status}
                  </span>
                  <span className="badge bg-purple-100 text-purple-700 capitalize">{event.type}</span>
                </div>
                <h3 className="font-semibold text-gray-900">{event.title}</h3>
                <p className="text-sm text-gray-500 mt-1 line-clamp-2">{event.description || 'No description'}</p>
                <div className="mt-4 space-y-1 text-sm text-gray-500">
                  <p>📅 {formatCalendarDateDMY(event.event_date)}</p>
                  {event.start_time && <p>🕐 {event.start_time} {event.end_time && `- ${event.end_time}`}</p>}
                  <p>📍 {event.venue || 'TBD'}</p>
                  <p>👥 {event.registration_count || 0} registered</p>
                </div>
                {event.fee > 0 && (
                  <p className="mt-2 font-medium text-primary-600">₹{parseFloat(event.fee).toFixed(2)}</p>
                )}
                <div className="mt-4 pt-4 border-t border-gray-200 flex items-center gap-2">
                  <button
                    onClick={() => handleViewEvent(event)}
                    className="text-primary-600 hover:text-primary-700 text-sm flex items-center gap-1"
                    title="View Details"
                  >
                    <HiEye className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleEditEvent(event)}
                    className="text-green-600 hover:text-green-700 text-sm flex items-center gap-1"
                    title="Edit Event"
                  >
                    <HiPencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(event, 'event')}
                    className="text-red-600 hover:text-red-700 text-sm flex items-center gap-1"
                    title="Delete Event"
                  >
                    <HiTrash className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))
          )
        ) : (
          trainings.length === 0 ? (
            <div className="col-span-full text-center py-12 text-gray-500">
              <HiOutlineAcademicCap className="w-12 h-12 mx-auto text-gray-300 mb-2" />
              <p>No training programs found</p>
            </div>
          ) : (
            trainings.map(training => (
              <div key={training.id} className="card relative">
                <div className="flex justify-between items-start mb-3">
                  <span className={`badge ${
                    training.status === 'upcoming' ? 'bg-blue-100 text-blue-700' :
                    training.status === 'completed' ? 'bg-green-100 text-green-700' :
                    training.status === 'ongoing' ? 'bg-amber-100 text-amber-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {training.status}
                  </span>
                  <span className="badge bg-amber-100 text-amber-700 capitalize">{training.type}</span>
                </div>
                <h3 className="font-semibold text-gray-900">{training.title}</h3>
                <p className="text-sm text-gray-500 mt-1 line-clamp-2">{training.description || 'No description'}</p>
                <div className="mt-4 space-y-1 text-sm text-gray-500">
                  <p>📅 {formatCalendarDateDMY(training.start_date)}</p>
                  {training.end_date && <p>📅 End: {formatCalendarDateDMY(training.end_date)}</p>}
                  <p>🎓 {training.mode}</p>
                  <p>👥 {training.registration_count || 0} registered</p>
                </div>
                {training.fee > 0 && (
                  <p className="mt-2 font-medium text-primary-600">₹{parseFloat(training.fee).toFixed(2)}</p>
                )}
                <div className="mt-4 pt-4 border-t border-gray-200 flex items-center gap-2">
                  <button
                    onClick={() => handleViewTraining(training)}
                    className="text-primary-600 hover:text-primary-700 text-sm flex items-center gap-1"
                    title="View Details"
                  >
                    <HiEye className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleEditTraining(training)}
                    className="text-green-600 hover:text-green-700 text-sm flex items-center gap-1"
                    title="Edit Training"
                  >
                    <HiPencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(training, 'training')}
                    className="text-red-600 hover:text-red-700 text-sm flex items-center gap-1"
                    title="Delete Training"
                  >
                    <HiTrash className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))
          )
        )}
      </div>

      {/* Event Form Modal */}
      {showEventForm && (
        <EventForm
          event={selectedEvent}
          onClose={() => {
            setShowEventForm(false);
            setSelectedEvent(null);
          }}
          onSuccess={handleFormSuccess}
        />
      )}

      {/* Training Form Modal */}
      {showTrainingForm && (
        <TrainingProgramForm
          training={selectedTraining}
          onClose={() => {
            setShowTrainingForm(false);
            setSelectedTraining(null);
          }}
          onSuccess={handleFormSuccess}
        />
      )}

      {/* Event Detail Modal */}
      {showEventDetail && detailEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <h3 className="text-lg font-semibold">Event Details</h3>
              <button
                onClick={() => {
                  setShowEventDetail(false);
                  setDetailEvent(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <HiX className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex gap-2 mb-4">
                <span className={`badge ${
                  detailEvent.status === 'upcoming' ? 'bg-blue-100 text-blue-700' :
                  detailEvent.status === 'completed' ? 'bg-green-100 text-green-700' :
                  detailEvent.status === 'ongoing' ? 'bg-amber-100 text-amber-700' :
                  'bg-gray-100 text-gray-700'
                }`}>
                  {detailEvent.status}
                </span>
                <span className="badge bg-purple-100 text-purple-700 capitalize">{detailEvent.type}</span>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">{detailEvent.title}</h2>
              {detailEvent.description && (
                <p className="text-gray-700">{detailEvent.description}</p>
              )}
              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                <div>
                  <label className="text-sm font-medium text-gray-500">Date</label>
                  <p className="text-gray-900">{formatCalendarDateDMY(detailEvent.event_date)}</p>
                </div>
                {(detailEvent.start_time || detailEvent.end_time) && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Time</label>
                    <p className="text-gray-900">
                      {detailEvent.start_time || 'TBD'} {detailEvent.end_time && `- ${detailEvent.end_time}`}
                    </p>
                  </div>
                )}
                <div>
                  <label className="text-sm font-medium text-gray-500">Mode</label>
                  <p className="text-gray-900 capitalize">{detailEvent.mode}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Venue</label>
                  <p className="text-gray-900">{detailEvent.venue || 'TBD'}</p>
                </div>
                {detailEvent.max_participants && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Max Participants</label>
                    <p className="text-gray-900">{detailEvent.max_participants}</p>
                  </div>
                )}
                <div>
                  <label className="text-sm font-medium text-gray-500">Fee</label>
                  <p className="text-gray-900 font-semibold">₹{parseFloat(detailEvent.fee || 0).toFixed(2)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Registrations</label>
                  <p className="text-gray-900">{detailEvent.registration_count || 0}</p>
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowEventDetail(false);
                  setDetailEvent(null);
                }}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Training Detail Modal */}
      {showTrainingDetail && detailTraining && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <h3 className="text-lg font-semibold">Training Program Details</h3>
              <button
                onClick={() => {
                  setShowTrainingDetail(false);
                  setDetailTraining(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <HiX className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex gap-2 mb-4">
                <span className={`badge ${
                  detailTraining.status === 'upcoming' ? 'bg-blue-100 text-blue-700' :
                  detailTraining.status === 'completed' ? 'bg-green-100 text-green-700' :
                  detailTraining.status === 'ongoing' ? 'bg-amber-100 text-amber-700' :
                  'bg-gray-100 text-gray-700'
                }`}>
                  {detailTraining.status}
                </span>
                <span className="badge bg-amber-100 text-amber-700 capitalize">{detailTraining.type}</span>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">{detailTraining.title}</h2>
              {detailTraining.description && (
                <p className="text-gray-700">{detailTraining.description}</p>
              )}
              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                <div>
                  <label className="text-sm font-medium text-gray-500">Start Date</label>
                  <p className="text-gray-900">{formatCalendarDateDMY(detailTraining.start_date)}</p>
                </div>
                {detailTraining.end_date && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">End Date</label>
                    <p className="text-gray-900">{formatCalendarDateDMY(detailTraining.end_date)}</p>
                  </div>
                )}
                {(detailTraining.start_time || detailTraining.end_time) && (
                  <>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Start Time</label>
                      <p className="text-gray-900">{detailTraining.start_time || 'TBD'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">End Time</label>
                      <p className="text-gray-900">{detailTraining.end_time || 'TBD'}</p>
                    </div>
                  </>
                )}
                <div>
                  <label className="text-sm font-medium text-gray-500">Mode</label>
                  <p className="text-gray-900 capitalize">{detailTraining.mode}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Venue</label>
                  <p className="text-gray-900">{detailTraining.venue || 'TBD'}</p>
                </div>
                {detailTraining.max_participants && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Max Participants</label>
                    <p className="text-gray-900">{detailTraining.max_participants}</p>
                  </div>
                )}
                <div>
                  <label className="text-sm font-medium text-gray-500">Fee</label>
                  <p className="text-gray-900 font-semibold">₹{parseFloat(detailTraining.fee || 0).toFixed(2)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Registrations</label>
                  <p className="text-gray-900">{detailTraining.registration_count || 0}</p>
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowTrainingDetail(false);
                  setDetailTraining(null);
                }}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && itemToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold">Delete {deleteType === 'event' ? 'Event' : 'Training Program'}</h3>
            </div>
            <div className="p-6">
              <p className="text-gray-700 mb-4">
                Are you sure you want to delete this {deleteType === 'event' ? 'event' : 'training program'}?
              </p>
              <div className="bg-gray-50 p-3 rounded-lg mb-4">
                <p className="font-medium">{itemToDelete.title}</p>
                <p className="text-sm text-gray-500 mt-1">
                  {deleteType === 'event' 
                    ? `Date: ${formatCalendarDateDMY(itemToDelete.event_date)}`
                    : `Start: ${formatCalendarDateDMY(itemToDelete.start_date)}`
                  }
                </p>
              </div>
              <p className="text-sm text-amber-600">
                ⚠️ This action cannot be undone.
              </p>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setItemToDelete(null);
                  setDeleteType(null);
                }}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Events;

